import { useState } from 'react';
import { SETTINGS_EXPORT_PATH, governedSettings, settingsAlways, tenantSettingsSave, type ApiError, type SettingsRuleRow, type TenantSettings } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { ErrLine, I, LOCKED_TAG, downloadFrom, pre, sx, useApi, useGuarded, usePage } from './kit';
import { RuleRows } from './RuleRows';

const RETENTIONS = ['7 years (recommended)', '10 years', '3 years', 'Indefinite'];
const CADENCES = ['Monthly conformity pack', 'Quarterly', 'On demand only'];
const CAPT6 = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:6px;';
const SELECT = 'display:flex;align-items:center;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;text-decoration:none;';

/** The server's rule sentences as the design's two-line rows (its PROPOSED rows when served). */
export function ruleRows(rows: SettingsRuleRow[] | undefined, lines: string[] | undefined) {
  return (rows ?? (lines ?? []).map((l) => ({ label: l, sub: '' }))).map((r) => ({ ...r, ...LOCKED_TAG }));
}

/** Data & Retention (design seIsRetention, T9513; dr* L22328-22336, seRetRows L22979). */
export function RetentionSection() {
  const { client } = useSession();
  const { changed } = usePage();
  const { wr, roReason } = useGuarded();
  // Retention and export cadence are Aithora's to set (PUT /api/settings is the operator
  // tier's): every other role reads them, marked Set by Aithora, never a dropdown that fails.
  const { role } = useConsoleRole();
  const aithoraSets = role === 'SUPER_ADMIN' || role === 'OPERATOR' || (role as string) === 'OWNER';
  const settings = useApi((c, s) => governedSettings(c, { signal: s }), []);
  const always = useApi((c, s) => settingsAlways(c, { signal: s }), []);
  const [exported, setExported] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const t = settings.data?.settings as TenantSettings | undefined;
  const retention = t?.retention || (t ? 'Not set' : '–');
  const cadence = t?.exportCadence || (t ? 'Not set' : '–');
  const next = (list: string[], cur: string) => list[(list.indexOf(cur) + 1) % list.length];
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  return (
    <div data-testid="settings-retention" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;max-width:820px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Data retention</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:15px;')}>How long sealed ledger entries and audit events are kept.</div>
      <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px 16px;margin-bottom:15px;')}>
        <div>
          <div style={sx(CAPT6)}>Ledger &amp; audit retention</div>
          {aithoraSets ? (
            <a href="#" role="button" aria-label={'Ledger and audit retention: ' + retention} {...wr(() => void run(async () => { await tenantSettingsSave(client, { body: { retention: next(RETENTIONS, retention) } }); settings.reload(); }))} style={sx(SELECT)}>
              {I(retention)}
              <span style={sx('flex:1;')} />
              <span style={sx('color:#54606C;')}>{'▾'}</span>
            </a>
          ) : (
            <div data-testid="retention-readonly" aria-label={'Ledger and audit retention: ' + retention + ', set by Aithora'} title="Set by Aithora" style={sx(SELECT + 'background:transparent;')}>
              {I(retention)}
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:16px;color:#54606C;white-space:nowrap;')}>Set by Aithora</span>
            </div>
          )}
        </div>
        <div>
          <div style={sx(CAPT6)}>Export cadence</div>
          {aithoraSets ? (
            <a href="#" role="button" aria-label={'Export cadence: ' + cadence} {...wr(() => void run(async () => { await tenantSettingsSave(client, { body: { exportCadence: next(CADENCES, cadence) } }); settings.reload(); }))} style={sx(SELECT)}>
              {I(cadence)}
              <span style={sx('flex:1;')} />
              <span style={sx('color:#54606C;')}>{'▾'}</span>
            </a>
          ) : (
            <div data-testid="retention-readonly" aria-label={'Export cadence: ' + cadence + ', set by Aithora'} title="Set by Aithora" style={sx(SELECT + 'background:transparent;')}>
              {I(cadence)}
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:16px;color:#54606C;white-space:nowrap;')}>Set by Aithora</span>
            </div>
          )}
        </div>
      </div>
      <div style={sx(CAPT6)}>On-demand export</div>
      <div style={sx('display:flex;align-items:center;height:38px;padding:0 4px 0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;')}>
        <span style={sx('font-size:16px;color:#22272E;')}>Download settings.json</span>
        <span style={sx('flex:1;')} />
        <a
          href="#"
          title={roReason ?? undefined}
          onClick={(e) => { pre(e); void run(async () => { await downloadFrom(client, SETTINGS_EXPORT_PATH, 'settings.json', 'POST'); setExported(true); }); }}
          style={sx('display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:6px;font-size:14px;font-weight:700;letter-spacing:.07em;text-decoration:none;flex:0 0 auto;white-space:nowrap;' + (exported ? 'background:rgba(47,138,111,.16);color:#12503C;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'))}
        >
          {I(exported ? 'EXPORTED' : 'EXPORT')}
        </a>
      </div>
      <ErrLine error={err ?? settings.error} />
      <div style={sx('font-size:16px;color:#54606C;margin-top:13px;line-height:1.55;')}>
        {I('Retention applies to sealed ledger entries and audit events, not to the conversation itself. Shortening it does not delete an entry already sealed, because an audit trail cannot be rewritten by the party being audited.')}
      </div>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:14px 0 4px;')}>Always kept this way</div>
      <RuleRows plain pad="8px 0" ro={roReason} rows={ruleRows(always.data?.dataRetentionRows, always.data?.dataRetention)} />
      {always.error && <ErrLine error={always.error} />}
    </div>
  );
}
